import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { makeApi } from '../api/code-forge/github-octokit';
import type { CodeForgeInterface, Comment } from '../api/code-forge/interface';
import { useConfigStore } from '../stores/config';

export const useApi = () => {
    const configStore = useConfigStore();
    const [api, setApi] = useState<CodeForgeInterface | undefined>(undefined);

    useEffect(() => {
        if (configStore.githubToken) makeApi(configStore.githubToken).then((a) => setApi(a));
        else setApi(undefined);
    }, [configStore.githubToken]);

    return api;
};

export const useListPullRequests = (options?: Parameters<CodeForgeInterface['listPullRequests']>[0]) => {
    const api = useApi();
    const [loading, setLoading] = useState(false);
    const [res, setRes] = useState<Awaited<ReturnType<CodeForgeInterface['listPullRequests']>>>({
        pullRequests: [],
        hasNext: false,
        hasPrev: false,
    });

    const { page, perPage, sortBy, sortDirection, state } = options || {};

    useEffect(() => {
        if (!api) return;

        setLoading(true);
        api.listPullRequests({ page, perPage, sortBy, sortDirection, state }).then((r) => {
            setRes(r);
            setLoading(false);
        });
    }, [api, page, perPage, sortBy, sortDirection, state]);

    return { ...res, loading };
};

export const useListPullRequestComments = (options: {
    pullRequestNumber: number;
    filter?: (comment: Comment) => boolean;
}) => {
    const api = useApi();
    const [comments, setComments] = useState<Comment[]>([]);
    const [commentsCount, setCommentsCount] = useState<number>(0);

    // When reloading the comments, ignore the cache.
    const ignoreCache = comments !== undefined;

    const fetchComments = useCallback(async () => {
        if (!api) return;

        const c = await api.getPullRequestComments(options.pullRequestNumber, { ignoreCache });
        setComments(options.filter ? c.comments.filter(options.filter) : c.comments);
        setCommentsCount(c.count);
    }, [api, options.pullRequestNumber, options.filter, ignoreCache]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    return { comments, commentsCount, refetch: fetchComments };
};

export const useFileCommitHistory = (options: { path: string; ref?: string; shouldFetch: boolean }) => {
    const api = useApi();

    const {
        data: history = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['fileCommitHistory', api?.repository, options.path, options.ref],
        queryFn: async () => {
            if (!api) throw new Error('API not initialized');

            return api.getFileCommitHistory({
                repository: api.repository,
                path: options.path,
                ref: options.ref,
            });
        },
        enabled: !!api && options.shouldFetch,
    });

    return { history, isLoading, errorMessage: error ? error.message : null, refetch };
};
